import { useState, useEffect, useRef } from 'react';
import { orderbookWs, type OrderbookData } from '../services/orderbookWebsocket';
import { formatPrice, formatSize, formatTotal } from '../utils/formatters';

interface OrderbookProps {
  symbol: string;
  dex: string | undefined;
}

// Track which price levels have changed for flash animation
interface FlashState {
  bids: Set<number>; // prices that changed
  asks: Set<number>;
}

export function Orderbook({ symbol, dex }: OrderbookProps) {
  const [orderbookData, setOrderbookData] = useState<OrderbookData | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [flashState, setFlashState] = useState<FlashState>({ bids: new Set(), asks: new Set() });
  const prevDataRef = useRef<OrderbookData | null>(null);
  const lastFlashTimeRef = useRef<number>(0);

  useEffect(() => {
    if (!symbol) return;

    // Clear old data immediately when symbol changes
    setOrderbookData(null);
    setIsConnected(true);

    console.log('Subscribing to orderbook:', symbol, 'dex:', dex);

    // Use a flag to prevent stale updates after unmount/symbol change
    let isActive = true;

    const handleOrderbookUpdate = (data: OrderbookData) => {
      // Only process updates if this effect is still active
      if (isActive) {
        // Flash animation: only on new best bid/ask (price improvement)
        // With 2 second cooldown to avoid excessive flashing
        const prevData = prevDataRef.current;
        const now = Date.now();
        const FLASH_COOLDOWN = 2000; // 2 seconds between flashes

        if (prevData && (now - lastFlashTimeRef.current) > FLASH_COOLDOWN) {
          const changedBids = new Set<number>();
          const changedAsks = new Set<number>();

          // Only flash when best bid improves (new higher bid)
          const prevBestBid = prevData.bids[0]?.price;
          const newBestBid = data.bids[0]?.price;
          if (newBestBid && prevBestBid && newBestBid > prevBestBid) {
            changedBids.add(newBestBid);
          }

          // Only flash when best ask improves (new lower ask)
          const prevBestAsk = prevData.asks[0]?.price;
          const newBestAsk = data.asks[0]?.price;
          if (newBestAsk && prevBestAsk && newBestAsk < prevBestAsk) {
            changedAsks.add(newBestAsk);
          }

          if (changedBids.size > 0 || changedAsks.size > 0) {
            lastFlashTimeRef.current = now;
            setFlashState({ bids: changedBids, asks: changedAsks });
            // Clear flash state after animation
            setTimeout(() => {
              setFlashState({ bids: new Set(), asks: new Set() });
            }, 500);
          }
        }
        prevDataRef.current = data;
        setOrderbookData(data);
      }
    };

    orderbookWs.subscribe(symbol, dex, handleOrderbookUpdate);

    return () => {
      // Mark this effect as inactive to prevent any pending updates
      isActive = false;
      orderbookWs.unsubscribe(handleOrderbookUpdate);
      setIsConnected(false);
    };
  }, [symbol, dex]);


  if (!orderbookData) {
    return (
      <div className="bg-[#0e0f10] rounded-md border border-[#2d3748] overflow-hidden flex flex-col xl:h-[641px]">
        {/* Header */}
        <div className="px-4 border-b border-[#2d3748] bg-[#0b0c0d] flex-shrink-0 flex items-center" style={{ minHeight: '40px' }}>
          <h3 className="text-white font-semibold text-sm">Order Book</h3>
        </div>

        {/* Column Headers */}
        <div className="grid grid-cols-3 px-3 py-2 text-[10px] font-semibold text-[#6c7884] uppercase tracking-wider border-b border-[#1f2937] flex-shrink-0">
          <div>Price</div>
          <div className="text-right">Size (USDC)</div>
          <div className="text-right">Total (USDC)</div>
        </div>

        {/* Loading State */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-[#6c7884] text-[13px]">
            {isConnected ? 'Loading...' : 'Select a market'}
          </div>
        </div>
      </div>
    );
  }

  const { bids, asks } = orderbookData;

  // Calculate max total for scaling background bars
  const maxBidTotal = bids.length > 0 ? Math.max(...bids.map(b => b.total)) : 0;
  const maxAskTotal = asks.length > 0 ? Math.max(...asks.map(a => a.total)) : 0;
  const maxTotal = Math.max(maxBidTotal, maxAskTotal);

  const getBarWidth = (total: number) => {
    if (maxTotal === 0) return 0;
    return (total / maxTotal) * 100;
  };
  const spread = asks.length > 0 && bids.length > 0 ? asks[0].price - bids[0].price : 0;
  const spreadPercent = bids.length > 0 ? ((spread / bids[0].price) * 100).toFixed(3) : '0.000';

  return (
    <div className="bg-[#0e0f10] rounded-md border border-[#2d3748] overflow-hidden flex flex-col xl:h-[641px]">
      {/* Header */}
      <div className="px-4 border-b border-[#2d3748] bg-[#0b0c0d] flex-shrink-0 flex items-center" style={{ minHeight: '40px' }}>
        <h3 className="text-white font-semibold text-sm">Order Book</h3>
      </div>

      {/* Column Headers */}
      <div className="grid grid-cols-3 px-3 py-2 text-[10px] font-semibold text-[#6c7884] uppercase tracking-wider border-b border-[#1f2937] flex-shrink-0">
        <div>Price</div>
        <div className="text-right">Size (USDC)</div>
        <div className="text-right">Total (USDC)</div>
      </div>

      {/* Content area - scrollable */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* Asks (Sells) - Best ask first, reversed for display (worst at top, best at bottom near spread) */}
        <div className="px-3 py-1">
          {asks.slice(0, 10).reverse().map((ask) => {
            const isFlashing = flashState.asks.has(ask.price);
            return (
              <div
                key={`ask-${ask.price}`}
                className={`relative grid grid-cols-3 py-1 hover:bg-[#1a1a1a] transition-colors cursor-pointer ${isFlashing ? 'flash-sell' : ''}`}
              >
                {/* Background bar - left aligned */}
                <div
                  className="absolute inset-y-0 left-0 bg-[#f6465d] opacity-[0.08] transition-all duration-150"
                  style={{ width: `${getBarWidth(ask.total)}%` }}
                />

                {/* Content */}
                <div className="relative text-[#f6465d] text-[12px] font-medium">
                  {formatPrice(ask.price, symbol)}
                </div>
                <div className="relative text-right text-[#b0b8c1] text-[12px]">
                  {formatSize(ask.size)}
                </div>
                <div className="relative text-right text-[#6c7884] text-[12px]">
                  {formatTotal(ask.total)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Spread */}
        <div className="px-3 py-2 bg-[#0b0c0d] border-y border-[#2d3748] flex-shrink-0">
          <div className="flex items-center justify-center gap-2">
            <span className="text-white text-[13px] font-medium">
              {formatPrice(bids[0]?.price || 0, symbol)}
            </span>
            <span className="text-[11px] text-[#6c7884]">
              ({spreadPercent}%)
            </span>
          </div>
        </div>

        {/* Bids (Buys) - Best bid first (highest price), displayed top to bottom */}
        <div className="px-3 py-1">
          {bids.slice(0, 10).map((bid) => {
            const isFlashing = flashState.bids.has(bid.price);
            return (
              <div
                key={`bid-${bid.price}`}
                className={`relative grid grid-cols-3 py-1 hover:bg-[#1a1a1a] transition-colors cursor-pointer ${isFlashing ? 'flash-buy' : ''}`}
              >
                {/* Background bar - left aligned */}
                <div
                  className="absolute inset-y-0 left-0 bg-[#0ecb81] opacity-[0.08] transition-all duration-150"
                  style={{ width: `${getBarWidth(bid.total)}%` }}
                />

                {/* Content */}
                <div className="relative text-[#0ecb81] text-[12px] font-medium">
                  {formatPrice(bid.price, symbol)}
                </div>
                <div className="relative text-right text-[#b0b8c1] text-[12px]">
                  {formatSize(bid.size)}
                </div>
                <div className="relative text-right text-[#6c7884] text-[12px]">
                  {formatTotal(bid.total)}
                </div>
              </div>
            );
          })}
        </div>
    </div>
    </div>
  );
}
